import React, { useRef, useState } from 'react';
import { CheckIcon, GlobeAltIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import axiosInstance from '../../../api/axiosInstance';
import logger from '../../../../utils/logger';

// Step 4 — teach the agent. Website crawl (server-side, bounded) and/or SOP
// document upload (multipart to /api/ai/knowledge/upload). Skippable — the
// step is marked either way so resume never traps a customer here.
const StepTeach = ({ status, wizardState, showNotification, markProgress, onDone, onBack }) => {
  const [url, setUrl] = useState(wizardState.website || status.website || '');
  const [crawling, setCrawling] = useState(false);
  const [crawlResult, setCrawlResult] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const crawl = async () => {
    if (!url.trim() || crawling) return;
    setCrawling(true);
    try {
      const res = await axiosInstance.post('/api/business/onboarding/crawl', { url: url.trim() }, { timeout: 90000 });
      setCrawlResult(res.data?.data || {});
      showNotification?.(res.data?.message || 'Website read', 'success');
    } catch (error) {
      logger.error('Crawl failed:', error);
      showNotification?.(error.response?.data?.message || 'Could not read that site', 'error');
    } finally {
      setCrawling(false);
    }
  };

  const upload = async (file) => {
    if (!file || uploading) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('document', file);
      const res = await axiosInstance.post('/api/ai/knowledge/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      setUploads((u) => [...u, { name: file.name, chars: res.data?.data?.chars }]);
      showNotification?.(res.data?.message || 'Document uploaded', 'success');
    } catch (error) {
      logger.error('Upload failed:', error);
      showNotification?.(error.response?.data?.message || 'Could not process that file', 'error');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const learned = !!crawlResult || uploads.length > 0 || status.knowledgeCount > 0;

  const next = async () => {
    // Crawl marks the step server-side already; cover the upload/skip paths.
    if (!await markProgress('knowledge')) return;
    onDone({});
  };

  const inputCls = 'flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500';

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
        Teach {wizardState.agentName || 'your agent'} about your business
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Point it at your website and drop in any SOPs, price lists, or FAQ docs. More context, better work.
      </p>

      {/* Website crawl */}
      <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <GlobeAltIcon className="w-5 h-5 text-orange-500" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">Read your website</span>
        </div>
        {!crawlResult ? (
          <div className="flex gap-2">
            <input className={inputCls} value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://yourcompany.com" onKeyDown={(e) => e.key === 'Enter' && crawl()} />
            <button onClick={crawl} disabled={crawling || !url.trim()}
              className="px-4 py-2 rounded-lg bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium disabled:opacity-50">
              {crawling ? 'Reading…' : 'Read site'}
            </button>
          </div>
        ) : (
          <div className="text-sm text-green-600 dark:text-green-400 flex items-center gap-2">
            <CheckIcon className="w-5 h-5" />
            Read {crawlResult.pages?.length || 1} page(s) — {Math.round((crawlResult.chars || 0) / 1000)}k characters learned
          </div>
        )}
      </div>

      {/* Document upload */}
      <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <DocumentArrowUpIcon className="w-5 h-5 text-orange-500" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">Upload documents</span>
          <span className="text-xs text-gray-400">pdf, docx, txt, md, csv · up to 10MB</span>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx,.txt,.md,.csv"
          onChange={(e) => upload(e.target.files?.[0])}
          className="block text-sm text-gray-500 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-orange-100 dark:file:bg-orange-900/30 file:text-orange-700 dark:file:text-orange-300 file:text-sm file:font-medium hover:file:bg-orange-200"
        />
        {uploading && <p className="text-xs text-gray-400 mt-2">Processing…</p>}
        {uploads.length > 0 && (
          <ul className="mt-2 space-y-1">
            {uploads.map((u, i) => (
              <li key={i} className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                <CheckIcon className="w-4 h-4" /> {u.name}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex justify-between">
        {onBack ? <button onClick={onBack} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Back</button> : <span />}
        <div className="flex gap-2">
          {!learned && (
            <button onClick={next} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
              Skip for now
            </button>
          )}
          <button
            onClick={next}
            disabled={!learned}
            className="px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-medium"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepTeach;
